import type { Href } from 'expo-router';
import type { AuthenticatedUser } from '@/hooks/useAuth';
import type { RedirectAfterLogin } from '@/lib/storage';
import type { RootStackParamList } from '@/types/navigation';

export type LeaguePrimarySection =
  | 'picks'
  | 'draft'
  | 'bracket'
  | 'standings'
  | 'rosters'
  | 'scores'
  | 'leaderboard'
  | 'stats'
  | 'chat'
  | 'payments'
  | 'admin'
  | 'settings'
  | 'squares';

type AuthRedirectTarget = keyof RootStackParamList | 'LeagueSettings' | 'SeasonEditor';

const withQuery = (pathname: string, params: Record<string, string | null | undefined>): Href => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (typeof value === 'string' && value.length > 0) {
      query.set(key, value);
    }
  });

  const queryString = query.toString();
  return (queryString.length > 0 ? `${pathname}?${queryString}` : pathname) as Href;
};

export const getLandingHref = (): Href => '/';
export const getForgotPasswordHref = (): Href => '/forgot-password' as Href;
export const getAuthHref = (params?: {
  redirectTo?: AuthRedirectTarget;
  inviteCode?: string;
  autoJoin?: boolean;
  startGoogleSignIn?: boolean;
}): Href =>
  withQuery('/auth', {
    redirectTo: params?.redirectTo,
    inviteCode: params?.inviteCode?.toUpperCase(),
    autoJoin: typeof params?.autoJoin === 'boolean' ? String(params.autoJoin) : undefined,
    startGoogleSignIn:
      typeof params?.startGoogleSignIn === 'boolean' ? String(params.startGoogleSignIn) : undefined,
  });
export const getIntroHref = (): Href => '/intro' as Href;
export const getCreateLeagueHref = (): Href => '/create-league';
export const getProfileHref = (): Href => '/profile';
export const getWeekManagementHref = (): Href => '/admin/week-management';
export const getBetsCreatorHref = (): Href => '/admin/bets-creator';
export const getFeedbackHref = (): Href => '/admin/feedback';
export const getJoinLeagueHref = (inviteCode?: string | null, autoJoin?: boolean): Href =>
  inviteCode
    ? withQuery(`/join/${inviteCode.toUpperCase()}`, {
        autoJoin: typeof autoJoin === 'boolean' ? String(autoJoin) : undefined,
      })
    : withQuery('/join', {
        autoJoin: typeof autoJoin === 'boolean' ? String(autoJoin) : undefined,
      });

export const getLeagueIndexHref = (
  leagueId: string,
  options?: {
    seasonId?: string | null;
    weekId?: string | null;
  },
): Href => {
  const normalizedId = String(leagueId);
  if (!normalizedId || normalizedId === 'undefined') {
    return getLandingHref();
  }
  return withQuery(`/league/${normalizedId}`, {
    season: options?.seasonId ?? undefined,
    week: options?.weekId ?? undefined,
  });
};

export const getLeagueHref = (
  leagueId: string,
  section: LeaguePrimarySection = 'picks',
  options?: {
    seasonId?: string | null;
    weekId?: string | null;
  },
): Href => {
  const normalizedLeagueId = String(leagueId);
  if (!normalizedLeagueId || normalizedLeagueId === 'undefined') {
    return getLandingHref();
  }
  const buildLeaguePath = (pathname: string): Href =>
    withQuery(pathname, {
      season: options?.seasonId ?? undefined,
      week: options?.weekId ?? undefined,
    });

  return buildLeaguePath(`/league/${normalizedLeagueId}/${section}`);
};

export const getLeagueSettingsHref = (leagueId: string): Href => `/league/${leagueId}/settings`;

export const getSeasonEditorHref = (
  leagueId: string,
  mode: 'create' | 'edit',
  seasonId?: string | null,
): Href =>
  withQuery(`/league/${leagueId}/season-editor`, {
    mode,
    seasonId: seasonId ?? undefined,
  });

export const normalizeStringParam = (value: string | string[] | undefined): string | undefined => {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

export const normalizeBooleanParam = (
  value: string | string[] | undefined,
  fallback = false,
): boolean => {
  const normalized = normalizeStringParam(value)?.trim().toLowerCase();
  if (!normalized) {
    return fallback;
  }
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
};

type DefaultDestination =
  | { name: 'League'; params: RootStackParamList['League'] }
  | { name: 'CreateLeague'; params?: undefined }
  | { name: 'Intro'; params?: undefined };

export function resolvePostLoginRoute(user?: AuthenticatedUser | null): DefaultDestination {
  const resolvedUser = user ?? null;
  const leagues = resolvedUser?.leagues?.filter(l => l && l.id && String(l.id) !== 'undefined') ?? [];
  const hasLeagues = leagues.length > 0;

  if (!hasLeagues) {
    return { name: 'Intro' };
  }

  if (resolvedUser?.defaultLeagueId && String(resolvedUser.defaultLeagueId) !== 'undefined') {
    const leagueId = String(resolvedUser.defaultLeagueId);
    const initialLeague = leagues.find(
      (league) => String(league.id) === leagueId
    );
    
    if (initialLeague) {
      const params: RootStackParamList['League'] = { 
        id: leagueId,
        initialLeague 
      };
      return {
        name: 'League',
        params,
      };
    }
  }

  const fallbackLeague = leagues[0];
  if (fallbackLeague?.id) {
    const leagueId = String(fallbackLeague.id);
    const params: RootStackParamList['League'] = { 
      id: leagueId,
      initialLeague: fallbackLeague
    };
    return {
      name: 'League',
      params,
    };
  }

  return { name: 'Intro' };
}

export const resolvePostLoginHref = (user?: AuthenticatedUser | null): Href => {
  const destination = resolvePostLoginRoute(user);

  if (destination.name === 'League') {
    return getLeagueIndexHref(destination.params.id);
  }

  if (destination.name === 'CreateLeague') {
    return getCreateLeagueHref();
  }

  return getIntroHref();
};

export const resolveAuthRedirect = (
  redirect: RedirectAfterLogin | null,
  fallbackInviteCode?: string | null,
): Href | null => {
  if (!redirect) {
    return null;
  }

  switch (redirect.routeName) {
    case 'JoinLeague': {
      const params = (redirect.params as RootStackParamList['JoinLeague']) ?? {};
      return getJoinLeagueHref(params?.inviteCode ?? fallbackInviteCode, params?.autoJoin ?? true);
    }
    case 'League': {
      const params = redirect.params as RootStackParamList['League'] | undefined;
      return params?.id ? getLeagueIndexHref(params.id) : null;
    }
    case 'CreateLeague':
      return getCreateLeagueHref();
    case 'Profile':
      return getProfileHref();
    case 'WeekManagement':
      return getWeekManagementHref();
    case 'BetsCreator':
      return getBetsCreatorHref();
    case 'Feedback':
      return getFeedbackHref();
    default:
      return null;
  }
};

export async function resolveInitialAppRoute(args: {
  isAuthenticated: boolean;
  user?: AuthenticatedUser | null;
  redirectAfterLogin?: RedirectAfterLogin | null;
  pendingInviteCode?: string | null;
}): Promise<string> {
  const { isAuthenticated, user, redirectAfterLogin, pendingInviteCode } = args;

  if (isAuthenticated) {
    const redirectHref = resolveAuthRedirect(redirectAfterLogin ?? null, pendingInviteCode);
    if (redirectHref) {
      return String(redirectHref);
    }
    const destination = resolvePostLoginRoute(user);
    if (destination.name === 'League') {
      return String(getLeagueIndexHref(destination.params.id));
    }
    if (destination.name === 'CreateLeague') {
      return String(getCreateLeagueHref());
    }
    return String(getIntroHref());
  }

  if (pendingInviteCode) {
    return String(getJoinLeagueHref(pendingInviteCode, true));
  }

  return String(getLandingHref());
}
